import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getReservations,
  type ReservationListItem,
  updateReservationStatus,
} from '../../../services/reservationsApi';
import {
  formatReservationCurrency,
  formatReservationDate,
} from '../utils/reservationMapper';
import { filterPendingReservations } from '../utils/reservationFilters';
import { useTranslation } from 'react-i18next';

const ReservationApplications: React.FC = () => {
  const { t } = useTranslation('other');
  const { getBranchName } = useAuth();
  const [rows, setRows] = useState<ReservationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadPendingReservations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getReservations({
        type: 'ticket',
        status: 'pending',
        page: 1,
        pageSize: 200,
      });
      // Yalnızca gerçek bekleyen kayıtları göster.
      // Odemesi basarili olanlar map katmaninda "Approved" oldugu icin bu listeye dusmez.
      setRows(filterPendingReservations(response.rows));
    } catch (err) {
      console.error('Failed to load reservation applications:', err);
      setError(t('reservations.applications.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPendingReservations();
  }, [loadPendingReservations]);

  const handleStatusUpdate = async (row: ReservationListItem, approved: boolean) => {
    try {
      setActionId(row.id);
      await updateReservationStatus(row.id, approved ? 'Onaylandı' : 'Reddedildi');
      setRows((prev) => prev.filter((item) => item.id !== row.id));
    } catch (err) {
      console.error('Failed to update reservation status:', err);
      setError(t('reservations.applications.errors.updateFailed'));
    } finally {
      setActionId(null);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">{t('reservations.applications.title')}</Typography>
        <Chip label={t('reservations.applications.total', { count: rows.length })} color="primary" variant="outlined" />
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <TableContainer sx={{ borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)', backgroundColor: '#fff' }}>
          <Table stickyHeader aria-label={t('reservations.applications.tableAriaLabel')}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.applications.table.reservationNo')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.applications.table.customer')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.applications.table.reservationDate')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.applications.table.branch')}</TableCell>
                <TableCell sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>{t('reservations.applications.table.amount')}</TableCell>
                <TableCell align="center" sx={{ backgroundColor: '#0b4ea2', color: '#fff', fontWeight: 700 }}>
                  {t('reservations.applications.table.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow
                  key={`${row.reservationType}-${row.id}`}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&:hover': { backgroundColor: '#f9f9f9' },
                  }}
                >
                  <TableCell component="th" scope="row">
                    {row.reservationId}
                  </TableCell>
                  <TableCell>{row.customerName || '-'}</TableCell>
                  <TableCell>{formatReservationDate(row.reservationDate)}</TableCell>
                  <TableCell>{getBranchName(row.branchId) || '-'}</TableCell>
                  <TableCell>{formatReservationCurrency(row.totalAmount)}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<CheckCircleIcon />}
                        disabled={actionId === row.id}
                        onClick={() => handleStatusUpdate(row, true)}
                      >
                        {t('reservations.applications.actions.approve')}
                      </Button>
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<CancelIcon />}
                        disabled={actionId === row.id}
                        onClick={() => handleStatusUpdate(row, false)}
                      >
                        {t('reservations.applications.actions.reject')}
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t('reservations.applications.empty')}
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default ReservationApplications;